import React from "react";
import { useTranslation } from "react-i18next";

import { languageName, languageNames, languageOptions } from "astrid-helpers/src/languages";

import Form from "../../forms/components/Form/Form";
import { options as workBokgruppeOptions } from "../../works/components/WorkMetaDataBokgruppeSelect/WorkMetaDataBokgruppeSelect";

import ArticleMetaDataBisacSelect from "../components/ArticleMetaDataForm/ArticleMetaDataBisacSelect";
import ArticleMetaDataBokgruppeSelect, {
	audiobookOptions,
	ebookOptions,
} from "../components/ArticleMetaDataForm/ArticleMetaDataBokgruppeSelect";
import ArticleMetaDataContributors from "../components/ArticleMetaDataForm/ArticleMetaDataContributors";
import ArticleMetaDataImprintSelect from "../components/ArticleMetaDataForm/ArticleMetaDataImprintSelect";
import ArticleMetaDataMainThemaSelect from "../components/ArticleMetaDataForm/ArticleMetaDataMainThemaSelect";
import ArticleMetaDataSerieNumberSelect from "../components/ArticleMetaDataForm/ArticleMetaDataSerieNumberSelect";
import ArticleMetaDataSerieSelect from "../components/ArticleMetaDataForm/ArticleMetaDataSerieSelect";
import ArticleMetaDataTagsSelect from "../components/ArticleMetaDataForm/ArticleMetaDataTagsSelect";
import ArticleMetaDataThemaCodes from "../components/ArticleMetaDataForm/ArticleMetaDataThemaCodes";

import useArticleAbridgedOptions from "./useArticleAbridgedOptions";

const bokgruppeOptions = [...workBokgruppeOptions, ...audiobookOptions, ...ebookOptions];

export default function useArticleMetadata({ publisher, disableSerie }) {
	const { t } = useTranslation();

	const abridgedOptions = useArticleAbridgedOptions();

	return [
		{
			name: "name",
			label: t("title", "Title"),
			field: <Form.Input />,
			format: (data) => data.name,
		},
		{
			name: "subtitle",
			label: t("subtitle", "Subtitle"),
			field: <Form.Input />,
			format: (data) => data.subtitle,
		},
		{
			name: "originalName",
			label: t("originalTitle", "Original title"),
			field: <Form.Input />,
			format: (data) => data.originalName,
		},
		{
			name: "language",
			label: t("language", "Language"),
			field: (
				<Form.Select options={languageOptions} limitedOptions={publisher?.languageOptions} allowEmpty search />
			),
			format: (data) => languageName(data.language),
		},
		{
			name: "originalLanguage",
			label: t("originalLanguage", "Original language"),
			field: <Form.Select options={languageOptions} allowEmpty search />,
			format: (data) => languageName(data.originalLanguage),
		},
		{
			name: "additionalLanguages",
			label: t("otherLanguages", "Other occurring languages"),
			field: <Form.Select options={languageOptions} multiple search />,
			format: (data) => languageNames(data.additionalLanguages),
		},
		{
			name: "serie",
			label: t("serie", "Series"),
			field: <ArticleMetaDataSerieSelect publisher={publisher} disabled={disableSerie} />,
			format: (data) => [data.serie?.name, data.serieNumber].filter(Boolean).join(" #"),
		},
		{
			name: "serieNumber",
			label: t("part", "Part"),
			field: <ArticleMetaDataSerieNumberSelect />,
		},
		{
			name: "contributors",
			label: t("contributors", "Contributors"),
			field: <ArticleMetaDataContributors header={null} />,
			style: { flex: "0 0 100%" },
		},
		{
			name: "imprint",
			label: t("imprint", "Imprint"),
			field: <ArticleMetaDataImprintSelect publisher={publisher} />,
			format: (data) => data.imprint?.name,
		},
		{
			name: "abridged",
			label: t("abridged", "Abridged"),
			field: <Form.Select options={abridgedOptions} />,
			format: (data) => (data.abridged === true ? t("yes", "Yes") : null),
		},
		{
			name: "synopsis",
			label: t("synopsis", "Synopsis"),
			field: <Form.TextArea style={{ fieldSizing: "content" }} />,
			style: { flex: "0 0 100%" },
			format: (data) => data.synopsis,
		},
		{
			name: "themaCodes",
			label: t("themaCodes", "Thema codes"),
			field: <ArticleMetaDataThemaCodes />,
			format: (data) => data.themaCodes?.join(", "),
		},
		{
			name: "mainThemaCode",
			label: t("mainThemaCode", "Main Thema code"),
			field: <ArticleMetaDataMainThemaSelect />,
			format: (data) => data.mainThemaCode,
		},
		{
			name: "bisacCodes",
			label: t("bisac", "BISAC"),
			field: <ArticleMetaDataBisacSelect />,
			format: (data) => data.bisacCodes?.join(", "),
		},
		{
			name: "tags",
			label: t("tags", "Tags"),
			field: <ArticleMetaDataTagsSelect />,
			style: { flex: "0 0 100%" },
			format: (data) => data.tags?.join(", "),
		},
		{
			name: "bokgruppe",
			label: t("bokgruppe", "Bokgruppe"),
			field: <ArticleMetaDataBokgruppeSelect />,
			format: (data) =>
				bokgruppeOptions.find((option) => option.value === data.bokgruppe)?.text ?? data.bokgruppe,
			condition: ({ language, channelIds }) => {
				return (
					["no", "nn", "nb"].includes(language) ||
					channelIds?.includes("bo9YS7NEoWqkrReOgGlG") ||
					channelIds?.includes("PGxlDFnh3lF88OqDZYo3")
				);
			},
		},
	];
}
